import { memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { IconHighlight, ModalLayout } from '@aimsos/ui';

import { useDismissModalOnRouteChange } from '../hooks';
import DashboardButton from './DashboardButton';
import { ModalFooter } from './parts';

import { Copy } from 'lucide-react';

/**
 * Confirm for duplicating a dashboard (CT-5183 / DS-3).
 *
 * Same shape as `ArchiveDashboardModal` beside it and as `DeleteWidgetModal`
 * in the widgets domain — see the note there for what this replaced. The only
 * differences are the icon, its tone, and the strings: duplicating creates
 * something, so the header reads primary rather than alert.
 */
const DuplicateDashboardModal = ({
  open = false,
  onClose,
  onConfirm,
  dashboardName = '',
  loading = false,
  error = '',
}) => {
  const { t } = useTranslation('dashboards');
  useDismissModalOnRouteChange(open, onClose);

  const displayName = `${dashboardName || ''}`.trim() || t('common.dashboardNameFallback');

  if (!open) return null;

  return (
    <ModalLayout
      isOpen={open}
      onClose={onClose}
      useBodyPortal
      maxWidthClassName="max-w-md"
      titlePrefix={<IconHighlight icon={Copy} bgColor="Primary" iconColor="PrimaryAlt" size="medium" />}
      title={t('modals.duplicate.title')}
      subtitle={displayName}
      closeDisabled={loading}
    >
      <div className="px-6 py-4">
        <p className="m-0 text-sm text-content-secondary">
          {t('modals.duplicate.body', { name: displayName })}
        </p>
      </div>

      <ModalFooter
        cancelLabel={t('actions.cancel')}
        onCancel={onClose}
        submitting={loading}
        submitError={error}
        primaryAction={
          <DashboardButton onClick={onConfirm}>{t('modals.duplicate.confirm')}</DashboardButton>
        }
      />
    </ModalLayout>
  );
};

DuplicateDashboardModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  dashboardName: PropTypes.string,
  loading: PropTypes.bool,
  /** Shown in the footer when the action failed; the dialog stays open. */
  error: PropTypes.string,
};

export default memo(DuplicateDashboardModal);
